import React, { Suspense, Component } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import strings from 'utils/strings'
import { Header, Paragraph, Button } from 'components/patterns'
import { Illustration } from 'components/ErrorBoundary/ErrorBoundary'

// Module-level constant so unit tests can mock window.location.reload without
// the inline arrow creating a new function reference on every render.
// Using a named function also avoids the jsdom instability of calling
// window.location.reload() in test environments that click the Refresh button.
const DEFAULT_RELOAD_ACTION = () => window.location.reload()

/**
 * Shared error body for modals — matches the Figma design (S-104, node 5136:15761).
 *
 * Spacing structure (per Figma S-104, nodes 5136:15760 and 5136:11294):
 *
 *   StyledContainer (flex-col, gap: 25px, align: center)
 *     ├─ Illustration at 50% size (183×175px)
 *     └─ StyledTextSection (flex-col, gap: 16px, px: 25px, align: center, w: 100%)
 *          ├─ Title (24px or 18px, centered, max-w: 510px)
 *          ├─ StyledBodyText (flex-col, gap: 22px, text-align: left)
 *          │    ├─ Paragraph 1 — "Your request could not be completed…"
 *          │    └─ Paragraph 2 — "Please refresh the page…"
 *          └─ Button (centered)
 *
 * The 25px gap comes from the standalone Error Modal's outer container
 * (gap-[25px] between illustration and text block). The 16px gap within the
 * text section comes from the in-modal content block (gap-[16px]).
 * The 22px gap between paragraphs matches one body line-height (blank-line spacer).
 *
 * The `size` prop controls text sizes based on modal size (per Figma spec):
 *   "large"  (default) — Header 24px, Body 16px  (Large / Extra-Large modals)
 *   "small"             — Header 18px, Body 14px  (Small / Medium modals)
 *
 * Usage:
 *   const [modalError, setModalError] = useState(null)
 *
 *   // In modal JSX — replace normal body when an error is set:
 *   {modalError ? <ModalErrorContent /> : <NormalContent />}
 *   {modalError ? <ModalErrorContent size="small" /> : <NormalContent />}
 */

/**
 * Minimal class-based error boundary around the lazy-loaded illustration.
 * Cannot import the app's ErrorBoundary here (circular dependency) so we use
 * a minimal inline boundary. If the illustration chunk fails to load (e.g.
 * after a deploy), the boundary silently hides the illustration so the rest
 * of the error UI (title, copy, Refresh button) still renders.
 */
class IllustrationBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      // Illustration chunk failed — render a placeholder to preserve layout
      return <StyledIllustrationPlaceholder />
    }
    return this.props.children
  }
}

IllustrationBoundary.propTypes = {
  children: PropTypes.node.isRequired,
}

const ModalErrorContent = ({ size, onAction, actionLabel, actionIcon }) => {
  const isSmall = size === 'small'
  // Default action is a full page reload — appropriate for mutations where
  // in-progress state may be corrupted. Callers can override this for
  // non-destructive contexts (e.g. a search failure inside a modal where
  // a reload would discard unsaved vehicle configuration edits).
  const handleAction = onAction || DEFAULT_RELOAD_ACTION
  // Default label and icon match the reload action. Callers that pass a
  // non-destructive onAction (e.g. closing the modal) should also pass a
  // matching label and icon so the button accurately describes what it does.
  const buttonLabel =
    actionLabel !== undefined ? actionLabel : strings.refresh_the_page
  const buttonIcon = actionIcon !== undefined ? actionIcon : 'refresh'
  return (
    <StyledContainer>
      {/* Illustration at 50% size — see StyledIllustrationWrapper comment */}
      <IllustrationBoundary>
        <Suspense fallback={<StyledIllustrationPlaceholder />}>
          <StyledIllustrationWrapper>
            <Illustration />
          </StyledIllustrationWrapper>
        </Suspense>
      </IllustrationBoundary>

      {/* Text + button section — gap: 16px between title, body, button */}
      <StyledTextSection isSmall={isSmall}>
        {isSmall ? (
          <StyledHeaderSmall>{strings.uh_oh_thats_not_right}</StyledHeaderSmall>
        ) : (
          <StyledHeader>{strings.uh_oh_thats_not_right}</StyledHeader>
        )}

        {/* Body paragraphs — gap matches the line-height of the body text so the
            blank line between them is exactly one line tall:
            large (16px text, 22px line-height) → gap 22px
            small (14px text, 18px line-height) → gap 18px
            Source: Figma uses a zero-width space <p> at the body line-height */}
        <StyledBodyText isSmall={isSmall}>
          <Paragraph size={isSmall ? undefined : 'large'}>
            {strings.api_error_body_line1}
          </Paragraph>
          <Paragraph size={isSmall ? undefined : 'large'}>
            {strings.api_error_body_line2}
          </Paragraph>
        </StyledBodyText>

        <StyledButtonContainer>
          <Button
            type="primary"
            onClick={handleAction}
            iconProps={{
              icon: buttonIcon,
              fill: 'whitePrimary',
              width: 12,
            }}
          >
            {buttonLabel}
          </Button>
        </StyledButtonContainer>
      </StyledTextSection>
    </StyledContainer>
  )
}

ModalErrorContent.propTypes = {
  /** Controls text sizes based on modal size per Figma spec (S-104):
   *  "large" (default) → Header 24px, Body 16px — for Large/Extra-Large modals
   *  "small"           → Header 18px, Body 14px — for Small/Medium modals
   */
  size: PropTypes.oneOf(['large', 'small']),
  /**
   * Optional override for the action button. Defaults to window.location.reload().
   * Pass a less-destructive action (e.g. closing the modal) when a full reload
   * would discard unsaved work — e.g. a search failure inside a modal over an
   * in-progress vehicle configuration.
   */
  onAction: PropTypes.func,
  /**
   * Optional label override for the action button. Defaults to strings.refresh_the_page.
   * Should be updated when onAction is not a page reload so the label accurately
   * describes what the button does (e.g. strings.close for a close-modal action).
   */
  actionLabel: PropTypes.string,
  /**
   * Optional icon name override for the action button. Defaults to 'refresh'.
   * Should match the actionLabel — e.g. use 'close' when actionLabel is strings.close.
   */
  actionIcon: PropTypes.string,
}

ModalErrorContent.defaultProps = {
  size: 'large',
  onAction: null,
  actionLabel: undefined,
  actionIcon: undefined,
}

/* Outer container — 25px gap between illustration and text section.
   Source: standalone Error Modal outer container (S-104, node 5136:15760, gap-[25px]) */
const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 25px;
`

/**
 * Constrains the 365×350px Illustration to 50% of its original size (183×175px).
 * The child (Illustration / Image div) is positioned absolutely at center and
 * scaled down with CSS transform. Using position+transform reclaims the layout
 * space to match the visual size (no 365px hole in the flow).
 */
const StyledIllustrationWrapper = styled.div`
  position: relative;
  width: 183px;
  height: 175px;
  overflow: hidden;

  > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.5);
  }
`

/* Matches the 50% illustration container so layout does not shift
   when the illustration chunk fails to load. */
const StyledIllustrationPlaceholder = styled.div`
  width: 183px;
  height: 175px;
`

/* Text + button block — 16px gap between title, body text, and button.
   max-width: 565px applies only for the large variant (wide modals like Create
   Feature) where the text needs to be constrained to avoid spanning too wide.
   For the small variant (PAR file, Create Collection — narrower modals) the
   text fills the available width, constrained naturally by the modal's own
   padding (ModalContent already applies padding: 16px 25px).
   Source: in-modal content block (S-104, node 5136:11294, gap-[16px]) */
const StyledTextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  ${({ isSmall }) => !isSmall && 'max-width: 565px;'}
`

/* Title — centered, max-width 510px per Figma (w-[510px]).
   width: 100% ensures text-align: center spans the full section width. */
const StyledHeader = styled(Header)`
  text-align: center;
  margin-bottom: 0;
  max-width: 510px;
  width: 100%;
`

/* Reduced title for small/medium modals — 18px per Figma (S-104, node 5136:16119).
   width: 100% ensures text-align: center spans the full section width. */
const StyledHeaderSmall = styled(Header)`
  text-align: center;
  margin-bottom: 0;
  font-size: 18px;
  line-height: 1.3;
  max-width: 510px;
  width: 100%;
`

/* Body paragraphs — left-aligned, gap matches body line-height (one blank line).
   large: 16px text, 22px line-height → gap 22px
   small: 14px text, 18px line-height → gap 18px
   Source: Figma body text block uses whitespace-pre-wrap with an empty paragraph
   (zero-width space) at the body line-height between the two lines. */
const StyledBodyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ isSmall }) => (isSmall ? '18px' : '22px')};
  text-align: left;
  width: 100%;
`

/* Button wrapper — centred; gap from StyledTextSection handles top spacing */
const StyledButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

export default ModalErrorContent
